import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import { FormattedMessage, injectIntl } from 'react-intl'
import {
  MdExpandMore,
  MdExpandLess,
  MdLink,
  MdOpenInNew,
  MdSend,
} from 'react-icons/md'
import { FaRobot } from 'react-icons/fa'
import {
  toggleChatWindow,
  setChatFullscreen,
  setChatWindowOpen,
} from '../actions'
import messages from '../messages'
import styles from '../styles.module.scss'
import { getChatScopeCode } from '../utils'

// ---------------------------------------------------------------------------
// Security helpers
// ---------------------------------------------------------------------------

const getSafeUrl = (url) => {
  try {
    const parsed = new URL(url)
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      return url
    }
  } catch {
    // invalid URL
  }
  return null
}

// ---------------------------------------------------------------------------
// Markdown rendering helpers
// ---------------------------------------------------------------------------

const decodeHtmlWhitespaceEntities = (text) => {
  if (!text) return text

  return text
    .replace(/&(nbsp|#160|#xa0);/gi, '\u00a0')
    .replace(/&(ensp|#8194|#x2002);/gi, '\u2002')
}

const renderInlineMarkdown = (text) => {
  if (!text) return text
  const parts = text.split(/(\*\*.*?\*\*)/)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>
    }
    return part
  })
}

const parseMarkdownTable = (tableLines) => {
  if (tableLines.length < 2) return null

  const trimMarkdownCell = (cell) => cell.replace(/^[ \t]+|[ \t]+$/g, '')

  // A pipe inside a cell comes escaped as \|, so it is kept out of the split and restored in the cell.
  const splitRow = (line) =>
    line
      .replace(/\\\|/g, '\u0000')
      .split('|')
      .slice(1, -1)
      .map((cell) =>
        trimMarkdownCell(cell)
          .split('\u0000')
          .join('|')
      )

  const headers = splitRow(tableLines[0])
  const alignRow = splitRow(tableLines[1])
  const alignments = alignRow.map((a) => {
    if (a.startsWith(':') && a.endsWith(':')) return 'center'
    if (a.endsWith(':')) return 'right'
    if (a.startsWith(':')) return 'left'
    return 'left'
  })
  const dataRows = tableLines.slice(2).map(splitRow)
  return { headers, alignments, dataRows }
}

const renderMarkdown = (text) => {
  if (!text) return text

  const normalizedText = decodeHtmlWhitespaceEntities(text)

  const lines = normalizedText.split('\n')
  const blocks = []
  let tableLines = []
  let nonTableLines = []

  const flushText = () => {
    if (nonTableLines.length > 0) {
      blocks.push({ type: 'text', content: nonTableLines.join('\n') })
      nonTableLines = []
    }
  }
  const flushTable = () => {
    if (tableLines.length > 0) {
      blocks.push({ type: 'table', lines: tableLines })
      tableLines = []
    }
  }

  for (const line of lines) {
    const headingMatch = line.match(/^(#{1,6})\s+(.*)/)
    if (headingMatch) {
      flushText()
      flushTable()
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        content: headingMatch[2],
      })
    } else if (line.trim().startsWith('|')) {
      flushText()
      tableLines.push(line)
    } else {
      flushTable()
      nonTableLines.push(line)
    }
  }
  flushText()
  flushTable()

  return blocks.map((block, blockIndex) => {
    if (block.type === 'heading') {
      const Tag = `h${block.level}`
      return (
        <Tag key={blockIndex} className={styles.markdownHeading}>
          {renderInlineMarkdown(block.content)}
        </Tag>
      )
    }

    if (block.type === 'table') {
      const parsed = parseMarkdownTable(block.lines)
      if (!parsed) return block.lines.join('\n')
      return (
        <div key={blockIndex} className={styles.tableWrapper}>
          <table className={styles.markdownTable}>
            <thead>
              <tr>
                {parsed.headers.map((header, i) => (
                  <th
                    key={i}
                    style={{ textAlign: parsed.alignments[i] || 'left' }}
                  >
                    {renderInlineMarkdown(header)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parsed.dataRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      style={{
                        textAlign: parsed.alignments[cellIndex] || 'left',
                      }}
                    >
                      {renderInlineMarkdown(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }

    // Text block with inline bold markdown
    const parts = block.content.split(/(\*\*.*?\*\*)/)
    return parts.map((part, partIndex) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={`${blockIndex}-${partIndex}`}>
            {part.slice(2, -2)}
          </strong>
        )
      }
      return <span key={`${blockIndex}-${partIndex}`}>{part}</span>
    })
  })
}

// ---------------------------------------------------------------------------
// Shared components
// ---------------------------------------------------------------------------

export const ChatMessage = ({ message }) => {
  const isUser = message.get('role') === 'user'
  const content = message.get('content')

  if (!content) return null

  const hasTable = !isUser && /\n\|/.test(content)

  const timestamp = new Date(message.get('timestamp')).toLocaleTimeString(
    'fi-FI',
    {
      hour: '2-digit',
      minute: '2-digit',
    }
  )

  return (
    <div
      className={`${styles.message} ${
        isUser ? styles.userMessage : styles.botMessage
      }`}
    >
      <div
        className={`${styles.messageContent} ${
          hasTable ? styles.hasTable : ''
        }`}
      >
        <div className={styles.messageText}>{renderMarkdown(content)}</div>
        {message.get('truncated') && (
          <div className={styles.truncatedNotice}>
            <FormattedMessage {...messages.chatAnswerTruncated} />
          </div>
        )}
        <div className={styles.messageTime}>{timestamp}</div>
      </div>
    </div>
  )
}

// Sources and suggestions render below the whole list, so they belong to the last message only.
// Reaching back for an older answer that had some put budget docs under a figures answer.
const getLatestAnswer = (chatMessages) => {
  const last = chatMessages.last()
  return last?.get('role') === 'assistant' ? last : null
}

export const SourcesList = ({ chatMessages }) => {
  const [showAll, setShowAll] = useState(false)

  const latestMessage = getLatestAnswer(chatMessages)

  const latestSources = latestMessage?.get('sources')
    ? latestMessage
        .get('sources')
        .toJS()
        .reduce((acc, source) => {
          if (!acc.some((s) => s.url === source.url)) {
            acc.push({ url: source.url, title: source.title })
          }
          return acc
        }, [])
    : null

  if (!latestSources || latestSources.length === 0) return null

  return (
    <div className={styles.sourcesList}>
      <button
        className={styles.showMoreButton}
        onClick={() => setShowAll(!showAll)}
      >
        <MdLink size={14} />
        {showAll ? (
          <FormattedMessage {...messages.hideSources} />
        ) : (
          <FormattedMessage
            {...messages.showSources}
            values={{ count: latestSources.length }}
          />
        )}
        {showAll ? <MdExpandLess size={15} /> : <MdExpandMore size={15} />}
      </button>
      {showAll && (
        <div className={styles.sourcesExpanded}>
          <div className={styles.sourcesListTitle}>
            <FormattedMessage {...messages.sources} />
          </div>
          <ul className={styles.sourcesListItems}>
            {latestSources.map((source, index) => {
              const safeUrl = getSafeUrl(source.url)
              return (
                <li key={index}>
                  {safeUrl ? (
                    <a href={safeUrl} target="_blank" rel="noopener noreferrer">
                      <span className={styles.sourceLinkText}>
                        {source.title || safeUrl}
                      </span>
                      <MdOpenInNew
                        size={12}
                        className={styles.sourceLinkIcon}
                      />
                    </a>
                  ) : (
                    <span className={styles.sourceLinkText}>
                      {source.title || source.url}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}

// Two elements, one visible and one for assistive technology.
//
// The live region has to stay mounted and only change its text: a region inserted into the DOM
// together with its text is announced inconsistently, and this indicator appears and disappears once
// per request. It cannot be the wrapper around the visible part either — the message list is a flex
// column with a gap, so an always-present wrapper would add a phantom gap whenever the status is
// empty. Bootstrap's .visually-hidden is position: absolute, which keeps it out of the flex flow
// entirely.
//
// The visible copy is aria-hidden so the status is not announced twice.
export const StatusIndicator = ({ status }) => (
  <>
    <div className="visually-hidden" role="status" aria-live="polite">
      {status || ''}
    </div>
    {status ? (
      <div className={styles.statusIndicator} aria-hidden="true">
        <div className={styles.statusSpinner} />
        <span className={styles.statusText}>{status}</span>
      </div>
    ) : null}
  </>
)

export const SuggestedQuestions = ({ chatMessages, onQuestionClick }) => {
  const suggestedQuestions = getLatestAnswer(chatMessages)?.get(
    'suggestedQuestions'
  )

  if (!suggestedQuestions || suggestedQuestions.size === 0) return null

  return (
    <div className={styles.suggestedQuestions}>
      {suggestedQuestions.map((question, index) => (
        <button
          key={index}
          className={styles.suggestedQuestionButton}
          onClick={() => onQuestionClick(question)}
        >
          {question}
        </button>
      ))}
    </div>
  )
}

export const ChatToggleButton = injectIntl(({ intl }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { customerCode, companyCode } = useParams()
  const isChatPage = location.pathname.endsWith('/chat')
  const scopeCode = getChatScopeCode({ customerCode, companyCode })

  const handleToggle = () => {
    if (isChatPage) {
      // On the legacy /chat route — just navigate away (ChatPage stays route-based)
      window.history.back()
    } else if (!scopeCode) {
      dispatch(toggleChatWindow())
    } else {
      dispatch(setChatWindowOpen(false))
      dispatch(setChatFullscreen(true))
      window.history.pushState({ chatFullscreen: true }, '')
    }
  }

  return (
    <button
      className={`${styles.chatToggleButton} ${
        isChatPage ? styles.chatToggleButtonActive : ''
      }`}
      onClick={handleToggle}
      aria-label={intl.formatMessage(messages.openChat)}
    >
      <FaRobot size={22} />
      <FormattedMessage {...messages.assistant} />
    </button>
  )
})

export const STATUS_MESSAGE_MAP = {
  'chat.status.fetchingHistory': messages.statusFetchingHistory,
  'chat.status.processingMessage': messages.statusProcessingMessage,
  'chat.status.searchingDocumentation': messages.statusSearchingDocumentation,
  'chat.status.fetchingFinancialData': messages.statusFetchingFinancialData,
  'chat.status.fetchingAccountScheme': messages.statusFetchingAccountScheme,
  'chat.status.fetchingBudgetData': messages.statusFetchingBudgetData,
  'chat.status.thinking': messages.statusThinking,
}

export const ChatInputForm = injectIntl(
  ({
    intl,
    inputMessage,
    onInputChange,
    onSubmit,
    isLoading,
    formClassName,
  }) => (
    <form className={formClassName} onSubmit={onSubmit}>
      <input
        type="text"
        className={styles.chatInput}
        placeholder={intl.formatMessage(messages.typeMessage)}
        value={inputMessage}
        onChange={onInputChange}
        disabled={isLoading}
        maxLength={2000}
      />
      <button
        type="submit"
        className={styles.sendButton}
        disabled={!inputMessage.trim() || isLoading}
        aria-label={intl.formatMessage(messages.sendMessage)}
      >
        <MdSend size={20} />
      </button>
    </form>
  )
)

export { default as ConversationHistory } from './ConversationHistory'
